import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, qs } from '../lib/api.js';
import { toError, useAction, useDebounced, useResource } from '../lib/useApi.js';
import { useAuth } from '../lib/auth.js';
import { designsEqual } from '../lib/design.js';
import type {
  ClientProfile,
  Design,
  DirectoryUser,
  LintResult,
  MergeFieldInfo,
  PreviewResponse,
  TemplateDetail,
  TemplateVersion,
} from '../lib/types.js';
import {
  Banner,
  bytes,
  Card,
  Check,
  Empty,
  ErrorState,
  Field,
  Loading,
  Modal,
  Pill,
  Segmented,
  StatusPill,
  When,
  useToast,
} from '../components/ui.js';
import { BlockOutline } from '../components/BlockOutline.js';
import { Inspector } from '../components/Inspector.js';
import { ClientPicker, PreviewGrid } from '../components/DevicePreview.js';
import { LintPanel, LintSummary } from '../components/LintPanel.js';
import { ImportModal } from '../components/ImportModal.js';

type Tab = 'design' | 'preview' | 'versions';

/**
 * Template editor.
 *
 * The version being edited is always a working copy in local state. Saving
 * creates a new version rather than mutating the one on screen, which is what
 * makes the history meaningful -- and it is why a published version opens
 * read-only until you explicitly start a new draft from it.
 */
export function TemplateEditorPage(): JSX.Element {
  const { id = '' } = useParams();
  const { me } = useAuth();
  const toast = useToast();

  const template = useResource<TemplateDetail>(`/api/templates/${id}`);
  const fields = useResource<{ fields: MergeFieldInfo[] }>('/api/preview/fields');
  const clients = useResource<{ clients: ClientProfile[]; defaultComparison: string[] }>(
    '/api/preview/clients',
  );

  const [tab, setTab] = useState<Tab>('design');
  const [versionId, setVersionId] = useState<string | null>(null);
  const [design, setDesign] = useState<Design | null>(null);
  const [baseline, setBaseline] = useState<Design | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showSave, setShowSave] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [importing, setImporting] = useState(false);
  /** Seeds the change note, so an imported version says so in its history. */
  const [importSummary, setImportSummary] = useState('');

  // Preview controls
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [darkMode, setDarkMode] = useState(false);
  const [withContext, setWithContext] = useState(true);
  const [previewUpn, setPreviewUpn] = useState('');

  const versions = template.data?.versions ?? [];
  const activeVersion: TemplateVersion | null =
    versions.find((v) => v.id === versionId) ?? versions[0] ?? null;

  // Load the newest version into the working copy the first time it arrives.
  useEffect(() => {
    if (!template.data || design !== null) return;
    const newest = template.data.versions[0];
    if (!newest) return;
    setVersionId(newest.id);
    setDesign(newest.design);
    setBaseline(newest.design);
  }, [template.data, design]);

  useEffect(() => {
    if (clients.data && selectedClients.length === 0) {
      setSelectedClients(clients.data.defaultComparison);
    }
  }, [clients.data, selectedClients.length]);

  const canEdit = Boolean(me?.capabilities.canEditTemplates);
  const canApprove = Boolean(me?.capabilities.canApprove);
  const canPublish = Boolean(me?.capabilities.canPublish);

  /**
   * A version is only editable while it is a draft or was rejected. Anything
   * further along is frozen: edit it by restoring it into a fresh draft.
   */
  const versionEditable =
    canEdit && Boolean(activeVersion && ['DRAFT', 'REJECTED'].includes(activeVersion.status));
  const readOnly = !versionEditable;

  const dirty = useMemo(
    () => Boolean(design && baseline && !designsEqual(design, baseline)),
    [design, baseline],
  );

  // Warn before losing unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // --- Live preview --------------------------------------------------------
  const debouncedDesign = useDebounced(design, 450);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<Error | null>(null);
  /**
   * Result of the explicit image-inspecting pass, together with the design it
   * was computed from.
   *
   * Keyed by design on purpose: it used to be a bare LintResult that, once set,
   * shadowed the fresh structural lint returned with every preview for the rest
   * of the session. Fixing a finding then left it sitting in the panel, which
   * makes the whole panel untrustworthy -- so it is dropped the moment the
   * design it describes stops being the design on screen.
   */
  const [fullLint, setFullLint] = useState<{ lint: LintResult; forDesign: Design } | null>(null);

  const renderPreview = useCallback(
    async (target: Design, opts: { deep?: boolean } = {}) => {
      try {
        const result = await api.post<PreviewResponse>('/api/preview', {
          design: target,
          clientIds: selectedClients.length ? selectedClients : undefined,
          darkMode,
          withMessageContext: withContext,
          fastLint: !opts.deep,
          ...(previewUpn ? { upn: previewUpn } : {}),
        });
        setPreview(result);
        setPreviewError(null);
        if (opts.deep) setFullLint({ lint: result.lint, forDesign: target });
      } catch (err) {
        setPreviewError(toError(err));
      }
    },
    [darkMode, previewUpn, selectedClients, withContext],
  );

  useEffect(() => {
    if (!debouncedDesign || selectedClients.length === 0) return;
    void renderPreview(debouncedDesign);
  }, [debouncedDesign, renderPreview, selectedClients.length]);

  // The structural lint comes back with every preview; the image-inspecting pass
  // is explicit because it makes network requests per image. The deeper result
  // wins only while it still describes the design on screen -- edit anything and
  // it is stale, so the live structural lint takes over until the full check is
  // run again.
  const lint = useMemo<LintResult | null>(() => {
    if (fullLint && design && designsEqual(fullLint.forDesign, design)) return fullLint.lint;
    return preview?.lint ?? null;
  }, [fullLint, design, preview]);

  const deepCheck = useAction(async () => {
    if (!design) return;
    await renderPreview(design, { deep: true });
    toast.success('Full check complete', 'Image files were downloaded and inspected.');
  });

  // --- Mutations -----------------------------------------------------------

  const save = useAction(async (changeNote: string) => {
    if (!design) return;
    const result = await api.post<{ version: TemplateVersion; lint: LintResult }>(
      `/api/templates/${id}/versions`,
      { design, ...(changeNote.trim() ? { changeNote: changeNote.trim() } : {}) },
    );
    setShowSave(false);
    setVersionId(result.version.id);
    setBaseline(design);
    await template.reload();
    toast.success(`Saved v${result.version.version}`, 'Saved as a new draft version.');
  });

  const submit = useAction(async () => {
    if (!activeVersion) return;
    await api.post(`/api/templates/${id}/versions/${activeVersion.id}/submit`, {});
    await template.reload();
    toast.success('Submitted for review', 'An approver can now sign it off.');
  });

  const review = useAction(async (action: 'approve' | 'reject', note: string) => {
    if (!activeVersion) return;
    await api.post(`/api/templates/${id}/versions/${activeVersion.id}/${action}`, {
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    setReviewAction(null);
    await template.reload();
    toast.success(action === 'approve' ? 'Approved' : 'Rejected');
  });

  const publish = useAction(async () => {
    if (!activeVersion) return;
    const result = await api.post<{ message: string }>(
      `/api/templates/${id}/versions/${activeVersion.id}/publish`,
      {},
    );
    await template.reload();
    toast.success('Published', result.message);
  });

  /**
   * Renaming is gated on canEdit, NOT on versionEditable.
   *
   * The name belongs to the template; the frozen-when-published rule protects
   * the *design* of a version that is live in people's mailboxes. Refusing a
   * rename because the live version is published would be locking the wrong
   * thing.
   */
  const rename = useAction(async (next: string) => {
    const trimmed = next.trim();
    if (!trimmed || trimmed === template.data?.name) return;
    await api.patch(`/api/templates/${id}`, { name: trimmed });
    await template.reload();
    toast.success('Renamed', `This template is now "${trimmed}".`);
  });

  const restore = useAction(async (sourceVersionId: string) => {
    const result = await api.post<{ version: TemplateVersion; message: string }>(
      `/api/templates/${id}/versions/${sourceVersionId}/restore`,
      {},
    );
    await template.reload();
    setVersionId(result.version.id);
    setDesign(result.version.design);
    setBaseline(result.version.design);
    setTab('design');
    toast.success('Restored as a new draft', result.message);
  });

  // --- Render --------------------------------------------------------------

  if (template.error) {
    return (
      <div className="page-body">
        <ErrorState error={template.error} onRetry={template.reload} />
      </div>
    );
  }
  if (!template.data || !design || !activeVersion) {
    return (
      <div className="page-body">
        <Loading rows={5} label="Loading template" />
      </div>
    );
  }

  const detail = template.data;
  const isLive = detail.publishedVersionId === activeVersion.id;

  return (
    <>
      <header className="page-head">
        <div>
          <div className="row wrap" style={{ marginBottom: 2 }}>
            <Link to="/templates" className="tiny muted">
              Templates
            </Link>
            <span className="tiny muted">/</span>
            <EditableTitle
              name={detail.name}
              canEdit={canEdit}
              onRename={(next) => void rename.run(next)}
              pending={rename.pending}
            />
            <StatusPill status={detail.status} />
          </div>
          <p className="sub">
            Editing v{activeVersion.version} <StatusPill status={activeVersion.status} />
            {isLive && <Pill tone="ok"> live</Pill>}
            {dirty && <Pill tone="warn"> unsaved changes</Pill>}
            {readOnly && !dirty && (
              <span className="tiny muted">
                {' '}
                — read-only. Restore it as a new draft from the Versions tab to change it.
              </span>
            )}
          </p>
        </div>

        <div className="page-actions">
          <Segmented<Tab>
            value={tab}
            onChange={setTab}
            options={[
              { value: 'design', label: 'Design' },
              { value: 'preview', label: 'Preview' },
              { value: 'versions', label: `Versions (${versions.length})` },
            ]}
          />

          {versionEditable && (
            <>
              <button
                onClick={() => setImporting(true)}
                title="Bring in a signature you already use, or a design exported from elsewhere"
              >
                Import
              </button>
              <button className="primary" disabled={!dirty} onClick={() => setShowSave(true)}>
                Save as new version
              </button>
            </>
          )}

          {activeVersion.status === 'DRAFT' || activeVersion.status === 'REJECTED' ? (
            <button disabled={dirty || submit.pending} onClick={() => void submit.run()} title={dirty ? 'Save your changes first' : undefined}>
              {submit.pending ? 'Submitting…' : 'Submit for review'}
            </button>
          ) : null}

          {activeVersion.status === 'IN_REVIEW' && canApprove && (
            <>
              <button className="primary" onClick={() => setReviewAction('approve')}>
                Approve
              </button>
              <button className="danger" onClick={() => setReviewAction('reject')}>
                Reject
              </button>
            </>
          )}

          {activeVersion.status === 'APPROVED' && canPublish && (
            <button className="primary" disabled={publish.pending} onClick={() => void publish.run()}>
              {publish.pending ? 'Publishing…' : 'Publish'}
            </button>
          )}
        </div>
      </header>

      <div className="page-body">
        {submit.error && <ErrorState error={submit.error} />}
        {publish.error && <ErrorState error={publish.error} />}
        {restore.error && <ErrorState error={restore.error} />}
        {previewError && <ErrorState error={previewError} />}

        {activeVersion.status === 'REJECTED' && activeVersion.reviewNote && (
          <Banner tone="danger">
            <strong>Rejected by {activeVersion.reviewer?.displayName ?? 'a reviewer'}</strong>
            <div>{activeVersion.reviewNote}</div>
          </Banner>
        )}

        {activeVersion.status === 'IN_REVIEW' && (
          <Banner tone="warn">
            <strong>Waiting for review</strong>
            <div className="tiny">
              {canApprove
                ? 'You can approve or reject this version. The author cannot approve their own work.'
                : 'An approver needs to sign this off before it can be published.'}
            </div>
          </Banner>
        )}

        {activeVersion.status === 'APPROVED' && (
          <Banner tone="info">
            <strong>Approved, not yet published</strong>
            <div className="tiny">
              Publishing makes this the version IT deploys. It does not push anything on its own.
            </div>
          </Banner>
        )}

        {lint?.hasBlockingIssues && (
          <Banner tone="danger">
            <strong>
              {lint.counts.error} blocking rendering issue{lint.counts.error === 1 ? '' : 's'}
            </strong>
            <div className="tiny">
              These must be fixed before the version can be submitted for review.
            </div>
          </Banner>
        )}

        {tab === 'design' && (
          <div className="editor">
            <div>
              <BlockOutline
                design={design}
                selectedId={selectedBlockId}
                findings={lint?.findings ?? []}
                readOnly={readOnly}
                onSelect={setSelectedBlockId}
                onChange={setDesign}
              />
            </div>

            <div className="col">
              <Card
                title="Live preview"
                actions={
                  <>
                    <LintSummary lint={lint} />
                    {preview && <span className="tiny muted">{bytes(preview.signature.bytes)}</span>}
                  </>
                }
                tight
              >
                {preview ? (
                  <div style={{ padding: 12 }}>
                    <PreviewGrid previews={preview.previews.slice(0, 2)} />
                  </div>
                ) : (
                  <div style={{ padding: 14 }}>
                    <Loading rows={3} label="Rendering preview" />
                  </div>
                )}
              </Card>

              <Card
                title="Rendering issues"
                actions={
                  <button className="sm" disabled={deepCheck.pending} onClick={() => void deepCheck.run()}>
                    {deepCheck.pending ? 'Checking images…' : 'Run full check'}
                  </button>
                }
                tight
              >
                <LintPanel
                  lint={lint}
                  selectedBlockId={selectedBlockId}
                  onSelectBlock={setSelectedBlockId}
                />
              </Card>
            </div>

            <div className="inspector-col">
              <Inspector
                design={design}
                selectedId={selectedBlockId}
                fields={fields.data?.fields ?? []}
                readOnly={readOnly}
                onChange={setDesign}
              />
            </div>
          </div>
        )}

        {tab === 'preview' && (
          <PreviewTab
            clients={clients.data?.clients ?? []}
            selectedClients={selectedClients}
            onSelectClients={setSelectedClients}
            darkMode={darkMode}
            onDarkMode={setDarkMode}
            withContext={withContext}
            onWithContext={setWithContext}
            previewUpn={previewUpn}
            onPreviewUpn={setPreviewUpn}
            preview={preview}
            lint={lint}
            design={design}
          />
        )}

        {tab === 'versions' && (
          <VersionsTab
            versions={versions}
            publishedVersionId={detail.publishedVersionId}
            activeVersionId={activeVersion.id}
            canRestore={canEdit}
            onOpen={(v) => {
              setVersionId(v.id);
              setDesign(v.design);
              setBaseline(v.design);
              setSelectedBlockId(null);
              setTab('design');
            }}
            onRestore={(v) => void restore.run(v.id)}
            restoring={restore.pending}
          />
        )}
      </div>

      {importing && (
        <ImportModal
          onClose={() => setImporting(false)}
          onApply={(imported, summary) => {
            setDesign(imported);
            setSelectedBlockId(null);
            setImportSummary(summary);
            setImporting(false);
            setTab('design');
            toast.success('Design imported', 'Review it, then save it as a new version.');
          }}
        />
      )}

      {showSave && (
        <SaveModal
          pending={save.pending}
          error={save.error}
          defaultNote={importSummary}
          onClose={() => setShowSave(false)}
          onSave={(note) => void save.run(note)}
        />
      )}

      {reviewAction && (
        <ReviewModal
          action={reviewAction}
          pending={review.pending}
          error={review.error}
          onClose={() => setReviewAction(null)}
          onSubmit={(note) => void review.run(reviewAction, note)}
        />
      )}
    </>
  );
}

// --- Preview tab -----------------------------------------------------------

/**
 * Click-to-edit title.
 *
 * A rename button beside the heading rather than a separate settings screen:
 * it is a one-word change and the name is right there. Enter or blur commits,
 * Escape abandons -- so a stray click cannot silently rename anything.
 */
function EditableTitle({
  name,
  canEdit,
  onRename,
  pending,
}: {
  name: string;
  canEdit: boolean;
  onRename: (next: string) => void;
  pending: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  // A rename from elsewhere, or a reload, must not be clobbered by a stale
  // draft still sitting in state.
  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);

  if (!canEdit) return <h1>{name}</h1>;

  if (!editing) {
    return (
      <span className="row" style={{ gap: 4 }}>
        <h1>{name}</h1>
        <button
          className="ghost sm"
          onClick={() => {
            setDraft(name);
            setEditing(true);
          }}
          title="Rename this template"
        >
          Rename
        </button>
      </span>
    );
  }

  const commit = (): void => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== name) onRename(draft);
    else setDraft(name);
  };

  return (
    <input
      autoFocus
      value={draft}
      disabled={pending}
      maxLength={120}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setDraft(name);
          setEditing(false);
        }
      }}
      aria-label="Template name"
      style={{ font: 'inherit', fontSize: 20, fontWeight: 650, width: 320, padding: '2px 6px' }}
    />
  );
}

function PreviewTab({
  clients,
  selectedClients,
  onSelectClients,
  darkMode,
  onDarkMode,
  withContext,
  onWithContext,
  previewUpn,
  onPreviewUpn,
  preview,
  lint,
  design,
}: {
  clients: ClientProfile[];
  selectedClients: string[];
  onSelectClients: (ids: string[]) => void;
  darkMode: boolean;
  onDarkMode: (v: boolean) => void;
  withContext: boolean;
  onWithContext: (v: boolean) => void;
  previewUpn: string;
  onPreviewUpn: (v: string) => void;
  preview: PreviewResponse | null;
  lint: LintResult | null;
  design: Design;
}): JSX.Element {
  const directory = useResource<{ users: DirectoryUser[] }>(
    `/api/directory/users${qs({ take: 100 })}`,
  );
  const [spread, setSpread] = useState<{
    cases: Array<{
      upn: string;
      displayName: string | null;
      jobTitle: string | null;
      reason: string;
      hiddenBlockIds: string[];
      preview: { document: string; label: string; viewportWidth: number; chrome: 'desktop' | 'browser' | 'phone'; darkMode: boolean; quirks: string[]; notes: string[]; clientId: string } | null;
    }>;
  } | null>(null);

  const runSpread = useAction(async () => {
    const result = await api.post<typeof spread>('/api/preview/spread', {
      design,
      clientId: selectedClients[0] ?? 'outlook-desktop-win',
    });
    setSpread(result);
  });

  const darkCapable = clients.filter((c) => selectedClients.includes(c.id) && c.supportsDarkMode);

  return (
    <>
      <div className="toolbar">
        <ClientPicker clients={clients} selected={selectedClients} onChange={onSelectClients} />
        <span className="spacer" />
        <label className="check" style={{ marginBottom: 0 }}>
          <input type="checkbox" checked={darkMode} onChange={(e) => onDarkMode(e.target.checked)} />
          <span>Dark mode</span>
        </label>
        <label className="check" style={{ marginBottom: 0 }}>
          <input
            type="checkbox"
            checked={withContext}
            onChange={(e) => onWithContext(e.target.checked)}
          />
          <span>Show in a message</span>
        </label>
        <select
          value={previewUpn}
          onChange={(e) => onPreviewUpn(e.target.value)}
          style={{ width: 240 }}
          aria-label="Preview as"
        >
          <option value="">Sample user</option>
          {(directory.data?.users ?? []).map((u) => (
            <option key={u.upn} value={u.upn}>
              {u.displayName ?? u.upn}
            </option>
          ))}
        </select>
      </div>

      {darkMode && darkCapable.length === 0 && (
        <Banner tone="info">
          None of the selected clients repaint the message background, so dark mode has no effect
          here. Add OWA, Outlook mobile or Gmail to see it.
        </Banner>
      )}

      {preview?.signature.unknownFields.length ? (
        <Banner tone="danger">
          <strong>Unrecognised merge fields</strong>
          <div className="tiny">
            {preview.signature.unknownFields.join(', ')} will be delivered literally.
          </div>
        </Banner>
      ) : null}

      {preview && preview.signature.hiddenBlockIds.length > 0 && (
        <Banner tone="info">
          <strong>{preview.signature.hiddenBlockIds.length} block(s) hidden for this user</strong>
          <div className="tiny">
            Their merge fields are empty, so the blocks were dropped rather than rendered blank:{' '}
            {preview.signature.hiddenBlockIds.join(', ')}.
          </div>
        </Banner>
      )}

      {preview ? (
        <PreviewGrid previews={preview.previews} />
      ) : (
        <Loading rows={4} label="Rendering previews" />
      )}

      <div className="grid cols-2 mt">
        <Card title="Rendering issues" tight>
          <LintPanel lint={lint} />
        </Card>

        <Card
          title="Stress test across real users"
          actions={
            <button className="sm" disabled={runSpread.pending} onClick={() => void runSpread.run()}>
              {runSpread.pending ? 'Rendering…' : 'Run'}
            </button>
          }
        >
          <p className="tiny muted">
            Renders the design for the directory users most likely to break it: the longest job title,
            the longest display name, and the person with the sparsest attributes.
          </p>
          {runSpread.error && <ErrorState error={runSpread.error} />}
          {spread?.cases.map((c) => (
            <div key={c.upn} className="mt">
              <div className="row wrap">
                <strong className="tiny">{c.displayName ?? c.upn}</strong>
                <Pill tone="outline">{c.reason}</Pill>
                {c.hiddenBlockIds.length > 0 && (
                  <Pill tone="warn">{c.hiddenBlockIds.length} block(s) hidden</Pill>
                )}
              </div>
              {c.jobTitle && <div className="tiny muted">{c.jobTitle}</div>}
              {c.preview && (
                <iframe
                  title={`${c.upn} preview`}
                  sandbox=""
                  srcDoc={c.preview.document}
                  style={{
                    width: '100%',
                    height: 190,
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    background: '#fff',
                    marginTop: 6,
                  }}
                />
              )}
            </div>
          ))}
        </Card>
      </div>

      {preview && (
        <Card title="Delivered HTML" className="mt" tight>
          <details>
            <summary style={{ cursor: 'pointer', padding: '10px 14px' }}>
              Show the exact HTML ({bytes(preview.signature.bytes)})
            </summary>
            <pre className="log" style={{ margin: 14 }}>
              {preview.signature.html}
            </pre>
          </details>
          <details>
            <summary style={{ cursor: 'pointer', padding: '10px 14px' }}>
              Show the plain-text alternative
            </summary>
            <pre className="log" style={{ margin: 14 }}>
              {preview.signature.plainText}
            </pre>
          </details>
        </Card>
      )}
    </>
  );
}

// --- Versions tab ----------------------------------------------------------

function VersionsTab({
  versions,
  publishedVersionId,
  activeVersionId,
  canRestore,
  onOpen,
  onRestore,
  restoring,
}: {
  versions: TemplateVersion[];
  publishedVersionId: string | null;
  activeVersionId: string;
  canRestore: boolean;
  onOpen: (v: TemplateVersion) => void;
  onRestore: (v: TemplateVersion) => void;
  restoring: boolean;
}): JSX.Element {
  if (versions.length === 0) {
    return (
      <Card>
        <Empty title="No versions">Save the design to create the first version.</Empty>
      </Card>
    );
  }

  return (
    <Card tight>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Version</th>
              <th>Status</th>
              <th>Change note</th>
              <th>Author</th>
              <th>Reviewer</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id}>
                <td className="cell-strong nowrap">
                  v{v.version}
                  {v.id === publishedVersionId && <Pill tone="ok"> live</Pill>}
                  {v.id === activeVersionId && <Pill tone="accent"> open</Pill>}
                </td>
                <td>
                  <StatusPill status={v.status} />
                </td>
                <td>
                  {v.changeNote ?? <span className="muted">—</span>}
                  {v.reviewNote && (
                    <div className="tiny muted">Review note: {v.reviewNote}</div>
                  )}
                </td>
                <td className="tiny">{v.author?.displayName ?? '—'}</td>
                <td className="tiny">{v.reviewer?.displayName ?? <span className="muted">—</span>}</td>
                <td className="tiny muted">
                  <When iso={v.createdAt} />
                </td>
                <td className="right nowrap">
                  <button className="sm" onClick={() => onOpen(v)}>
                    Open
                  </button>
                  <a
                    className="btn sm"
                    style={{ marginLeft: 4 }}
                    href={`/api/templates/${v.templateId}/versions/${v.id}/export`}
                    download
                    title="Download this version's design as a file you can import elsewhere"
                  >
                    Export
                  </a>
                  {canRestore && (
                    <button
                      className="sm"
                      disabled={restoring}
                      style={{ marginLeft: 4 }}
                      onClick={() => onRestore(v)}
                      title="Copies this version's design into a new draft. Nobody's mailbox changes."
                    >
                      Restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// --- Modals ----------------------------------------------------------------

function SaveModal({
  pending,
  error,
  defaultNote,
  onClose,
  onSave,
}: {
  pending: boolean;
  error: Error | null;
  defaultNote?: string;
  onClose: () => void;
  onSave: (note: string) => void;
}): JSX.Element {
  const [note, setNote] = useState(defaultNote ?? '');
  return (
    <Modal
      title="Save as a new version"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={pending} onClick={() => onSave(note)}>
            {pending ? 'Saving…' : 'Save version'}
          </button>
        </>
      }
    >
      {error && <ErrorState error={error} />}
      <p className="tiny muted">
        Versions are never overwritten. This creates the next draft, leaving the current live version
        untouched until a new one is published.
      </p>
      <Field label="What changed?" hint="Shown in the version history and the audit trail.">
        <textarea
          rows={3}
          value={note}
          autoFocus
          placeholder="e.g. New accent colour for the rebrand"
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </Modal>
  );
}

function ReviewModal({
  action,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  action: 'approve' | 'reject';
  pending: boolean;
  error: Error | null;
  onClose: () => void;
  onSubmit: (note: string) => void;
}): JSX.Element {
  const [note, setNote] = useState('');
  const rejecting = action === 'reject';

  return (
    <Modal
      title={rejecting ? 'Reject this version' : 'Approve this version'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button
            className={rejecting ? 'danger' : 'primary'}
            disabled={pending || (rejecting && !note.trim())}
            onClick={() => onSubmit(note)}
          >
            {pending ? 'Working…' : rejecting ? 'Reject' : 'Approve'}
          </button>
        </>
      }
    >
      {error && <ErrorState error={error} />}
      <p className="tiny muted">
        {rejecting
          ? 'The author will see your note and can revise the draft.'
          : 'Approving records you as the reviewer. Publishing is a separate step.'}
      </p>
      <Field label={rejecting ? 'Reason (required)' : 'Note (optional)'}>
        <textarea rows={3} value={note} autoFocus onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}
